import React, { useEffect, useState, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { preemptiveWakeup } from '../utils/backendWakeup';
import { useVisitorTracking } from '../hooks/useVisitorTracking';
import { logSensitive, logDebug } from '../utils/logger';
import { API_CONFIG } from '../config/apiConfig';
import { safeFetch, isExtensionError } from '../utils/extensionErrorHandler';

// Hide sensitive API URL from production console
logSensitive('API URL:', API_CONFIG.BASE_URL);

const LONG_PRESS_DURATION = 5000; // 5 seconds

const Header = ({ isHidden = false }) => {
  const { ref } = useVisitorTracking('header', {
    threshold: 0.8,
    minDuration: 1,
    trackOnMount: true
  });

  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminLoginMessage, setAdminLoginMessage] = useState("");
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);
  const navigate = useNavigate();

  // Start long press timer
  const startLongPress = (e) => {
    // Only prevent default for mouse events, not touch events
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      setShowAdminModal(true);
      longPressTriggered.current = true;
    }, LONG_PRESS_DURATION);
  };

  // Cancel long press timer
  const cancelLongPress = (e) => {
    clearTimeout(longPressTimer.current);
    if (!longPressTriggered.current && e.type === 'click') {
      // Only reload if not a long press
      window.location.reload();
    }
  };

  // --- Admin Modal Login Handler ---
  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminLoginMessage("");

    // Wake up backend before attempting login
    setAdminLoginMessage('Connecting to server...');
    await preemptiveWakeup();

    try {
      const response = await safeFetch(API_CONFIG.ENDPOINTS.ADMIN_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: adminEmail, password: adminPassword })
      });
      const data = await response.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        setAdminLoginMessage('Login successful! Redirecting...');
        setTimeout(() => {
          setShowAdminModal(false);
          navigate('/admin/dashboard');
        }, 1000);
      } else {
        setAdminLoginMessage(data.message || 'Login failed');
      }
    } catch (err) {
      // Don't show network errors for extension-related issues
      if (!isExtensionError(err)) {
        setAdminLoginMessage('Network error');
      } else {
        logDebug('Extension error suppressed in Header:', err.message);
      }
    }
  };

  const effectiveHidden = isHidden && !showAdminModal;

  const [isContactVisible, setIsContactVisible] = useState(false);
  const [isMessageSent, setIsMessageSent] = useState(false);

  // Listen for successful message sent
  useEffect(() => {
    const handleMessageSent = () => {
      setIsMessageSent(true);
    };

    window.addEventListener('contactMessageSent', handleMessageSent);
    return () => window.removeEventListener('contactMessageSent', handleMessageSent);
  }, []);

  // Observer for contact section to control floating button visibility
  useEffect(() => {
    const contactOverlay = document.querySelector('.contact-overlay');
    if (!contactOverlay) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Hides only when the full contact-overlay is in view
        if (entry.isIntersecting) {
          setIsContactVisible(true);
        } else {
          setIsContactVisible(false);
        }
      },
      { threshold: 1.0 }
    );

    observer.observe(contactOverlay);
    return () => observer.disconnect();
  }, []);

  // Toggle a body class so layout can respond when hidden
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const body = document.body;
    if (effectiveHidden) {
      body.classList.add('mobile-header-hidden-body');
    } else {
      body.classList.remove('mobile-header-hidden-body');
    }
    return () => body.classList.remove('mobile-header-hidden-body');
  }, [effectiveHidden]);

  // If header is visible and contact section is NOT visible, both buttons appear on mobile
  const bothButtonsVisibleMobile = !effectiveHidden && !isContactVisible;

  return (
    <>
      <header ref={ref} className={effectiveHidden ? 'mobile-header-hidden' : ''}>
        <div className="logo">
          <a
            href="/"
            onMouseDown={startLongPress}
            onMouseUp={cancelLongPress}
            onMouseLeave={cancelLongPress}
            onTouchStart={startLongPress}
            onTouchEnd={cancelLongPress}
            onClick={cancelLongPress}
          >
            <img src="/logo.PNG" alt="Logo" className="logo-img" />
          </a>
        </div>
        <a
          href="https://www.canva.com/design/DAGNjKc0Cr0/nJ-kiMZTpFhVUD6B6nyPYg/view?utm_content=DAGNjKc0Cr0&utm_campaign=designshare&utm_medium=link2&utm_source=uniquelinks&utlId=h6802188a93"
          className={`cv-button ${bothButtonsVisibleMobile ? 'mobile-shrink-btn' : ''}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Get CV
        </a>
        {showAdminModal && typeof document !== 'undefined' && createPortal(
          <div className="admin-modal-overlay" onClick={() => setShowAdminModal(false)}>
            <div className="admin-modal" onClick={e => e.stopPropagation()}>
              <button className="admin-modal-close" onClick={() => setShowAdminModal(false)}>&times;</button>
              <h2 className="admin-modal-title">Admin Login</h2>
              <form className="admin-modal-form" onSubmit={handleAdminLogin} autoComplete="off">
                <label htmlFor="admin-email">Email</label>
                <input type="email" id="admin-email" name="email" autoComplete="username" required value={adminEmail} onChange={e => setAdminEmail(e.target.value)} />
                <label htmlFor="admin-password">Password</label>
                <input type="password" id="admin-password" name="password" autoComplete="current-password" required value={adminPassword} onChange={e => setAdminPassword(e.target.value)} />
                <button type="submit" className="admin-modal-submit">Login</button>
              </form>
              {adminLoginMessage && <div style={{ marginTop: 8, color: adminLoginMessage.includes('success') ? 'green' : 'red' }}>{adminLoginMessage}</div>}
            </div>
          </div>,
          document.body
        )}
      </header>
      <button
        className={`contact-floating-btn ${effectiveHidden ? 'mobile-header-hidden' : ''} ${isContactVisible ? 'contact-floating-btn-hidden' : ''} ${isMessageSent ? 'contact-floating-btn-sent' : ''} ${bothButtonsVisibleMobile ? 'mobile-shrink-btn' : ''}`}
        onClick={() => {
          if (isMessageSent) return; // Prevent scrolling if already sent

          const contactSection = document.getElementById('contact');
          if (contactSection) {
            contactSection.scrollIntoView({ behavior: 'smooth' });

            // Add a temporary highlight class to the overlay to draw attention
            const overlay = contactSection.querySelector('.contact-overlay');
            if (overlay) {
              overlay.classList.add('highlight-pulse');
              setTimeout(() => {
                overlay.classList.remove('highlight-pulse');
              }, 1500);
            }
          }
        }}
        style={isMessageSent ? { cursor: 'not-allowed' } : {}}
      >
        {isMessageSent ? 'Sent ✓' : 'Contact'}
      </button>
    </>
  );
};

export default memo(Header);
